import type { Category, Completion, Habit, Setting } from "../db/schema";

// ─── Theme ───

export type ThemeName = "light" | "dark" | "amoled" | "system";

// ─── Validation helpers ───

const VALID_THEMES: Set<ThemeName> = new Set(["light", "dark", "amoled", "system"]);
const VALID_DENSITIES: Set<AppSettings["density"]> = new Set(["auto", "lean", "dense"]);
const HEX_COLOR_RE = /^#[0-9a-fA-F]{6}$/;

/** Parse an integer with NaN fallback and range clamping. */
export function parseIntSafe(raw: string, fallback: number, min: number, max: number): number {
  const n = parseInt(raw, 10);
  if (Number.isNaN(n)) return fallback;
  return Math.max(min, Math.min(max, n));
}

function validateTheme(raw: string | undefined): ThemeName {
  return raw !== undefined && VALID_THEMES.has(raw as ThemeName)
    ? (raw as ThemeName)
    : DEFAULT_SETTINGS.theme;
}

function validateDensity(raw: string | undefined): AppSettings["density"] {
  return raw !== undefined && VALID_DENSITIES.has(raw as AppSettings["density"])
    ? (raw as AppSettings["density"])
    : DEFAULT_SETTINGS.density;
}

function validateHexColor(raw: string | undefined): string {
  return raw !== undefined && HEX_COLOR_RE.test(raw) ? raw : DEFAULT_SETTINGS.accentColor;
}

function parseIntSetting(
  raw: string | undefined,
  fallback: number,
  min: number,
  max: number,
): number {
  return raw !== undefined ? parseIntSafe(raw, fallback, min, max) : fallback;
}

// ─── Settings ───

export interface AppSettings {
  theme: ThemeName;
  firstDayOfWeek: number;
  dayBoundaryHour: number;
  showStreaks: boolean;
  density: "auto" | "lean" | "dense";
  graceDaysPerWeek: number;
  accentColor: string;
  onboardingCompleted: boolean;
  scoreRevealed: boolean;
  dismissedMilestones: string[];
  lastActiveDate: string;
  addMoreDismissed: boolean;
  isLightDayToday: boolean;
}

export const DEFAULT_SETTINGS: AppSettings = {
  theme: "system",
  firstDayOfWeek: 1,
  dayBoundaryHour: 0,
  showStreaks: false,
  density: "auto",
  graceDaysPerWeek: 1,
  accentColor: "#2563eb",
  onboardingCompleted: false,
  scoreRevealed: false,
  dismissedMilestones: [],
  lastActiveDate: "",
  addMoreDismissed: false,
  isLightDayToday: false,
};

export function parseDismissedMilestones(raw: string | undefined): string[] {
  if (!raw) return [];
  try {
    const parsed: unknown = JSON.parse(raw);
    if (Array.isArray(parsed)) return parsed as string[];
  } catch {
    /* ignore malformed JSON */
  }
  return [];
}

export function parseSettings(rows: Setting[], today: string): AppSettings {
  const map = new Map(rows.map((r) => [r.key, r.value]));
  const raw = (key: string) => map.get(key);
  return {
    theme: validateTheme(raw("theme")),
    firstDayOfWeek: parseIntSetting(raw("firstDayOfWeek"), DEFAULT_SETTINGS.firstDayOfWeek, 0, 6),
    dayBoundaryHour: parseIntSetting(
      raw("dayBoundaryHour"),
      DEFAULT_SETTINGS.dayBoundaryHour,
      0,
      23,
    ),
    showStreaks: raw("showStreaks") === "true",
    density: validateDensity(raw("density")),
    graceDaysPerWeek: parseIntSetting(
      raw("graceDaysPerWeek"),
      DEFAULT_SETTINGS.graceDaysPerWeek,
      0,
      7,
    ),
    accentColor: validateHexColor(raw("accentColor")),
    onboardingCompleted: raw("onboardingCompleted") === "true",
    scoreRevealed: raw("scoreRevealed") === "true",
    dismissedMilestones: parseDismissedMilestones(raw("dismissedMilestones")),
    lastActiveDate: raw("lastActiveDate") ?? DEFAULT_SETTINGS.lastActiveDate,
    addMoreDismissed: raw("addMoreDismissed") === "true",
    isLightDayToday: raw(`lightDay:${today}`) === "true",
  };
}

// ─── State ───

export interface HabitsState {
  phase: "loading" | "ready" | "error";
  habits: Habit[];
  completions: Completion[];
  categories: Category[];
  settings: AppSettings;
  today: string;
}

export function createInitialState(): HabitsState {
  return {
    phase: "loading",
    habits: [],
    completions: [],
    categories: [],
    settings: DEFAULT_SETTINGS,
    today: "",
  };
}

// ─── Actions ───

export type HabitsAction =
  | {
      type: "INIT_SUCCESS";
      habits: Habit[];
      completions: Completion[];
      categories: Category[];
      settings: AppSettings;
      today: string;
    }
  | { type: "UPSERT_COMPLETION"; completion: Completion }
  | { type: "UNDO_COMPLETION"; habitId: string; effectiveDate: string }
  | { type: "HABIT_CREATED"; habit: Habit }
  | { type: "HABIT_UPDATED"; habit: Habit }
  | { type: "HABIT_ARCHIVED"; habitId: string }
  | { type: "HABIT_DELETED"; habitId: string }
  | { type: "SETTINGS_CHANGED"; settings: AppSettings }
  | { type: "UPDATE_NOTE"; habitId: string; effectiveDate: string; note: string | null }
  | { type: "REORDER"; habits: Habit[] }
  | { type: "CATEGORY_CREATED"; category: Category }
  | { type: "CATEGORY_UPDATED"; category: Category }
  | { type: "CATEGORY_DELETED"; categoryId: string }
  | { type: "CATEGORIES_REORDERED"; categories: Category[] }
  | {
      type: "REFRESH";
      habits: Habit[];
      completions: Completion[];
      categories: Category[];
      settings?: AppSettings;
    };

function upsertCompletion(completions: Completion[], completion: Completion): Completion[] {
  const idx = completions.findIndex(
    (c) => c.habit_id === completion.habit_id && c.effective_date === completion.effective_date,
  );
  const result = [...completions];
  if (idx >= 0) {
    result[idx] = completion;
  } else {
    result.push(completion);
  }
  return result;
}

export function habitsReducer(state: HabitsState, action: HabitsAction): HabitsState {
  switch (action.type) {
    case "INIT_SUCCESS":
      return {
        phase: "ready",
        habits: action.habits,
        completions: action.completions,
        categories: action.categories,
        settings: action.settings,
        today: action.today,
      };

    case "UPSERT_COMPLETION":
      return { ...state, completions: upsertCompletion(state.completions, action.completion) };

    case "UNDO_COMPLETION":
      return {
        ...state,
        completions: state.completions.filter(
          (c) => !(c.habit_id === action.habitId && c.effective_date === action.effectiveDate),
        ),
      };

    case "HABIT_CREATED":
      return { ...state, habits: [...state.habits, action.habit] };

    case "HABIT_UPDATED":
      return {
        ...state,
        habits: state.habits.map((h) => (h.id === action.habit.id ? action.habit : h)),
      };

    case "HABIT_ARCHIVED":
      return {
        ...state,
        habits: state.habits.filter((h) => h.id !== action.habitId),
      };

    case "HABIT_DELETED":
      return {
        ...state,
        habits: state.habits.filter((h) => h.id !== action.habitId),
      };

    case "SETTINGS_CHANGED":
      return { ...state, settings: action.settings };

    case "UPDATE_NOTE":
      return {
        ...state,
        completions: state.completions.map((c) =>
          c.habit_id === action.habitId && c.effective_date === action.effectiveDate
            ? { ...c, note: action.note }
            : c,
        ),
      };

    case "REORDER":
      return { ...state, habits: action.habits };

    case "CATEGORY_CREATED":
      return { ...state, categories: [...state.categories, action.category] };

    case "CATEGORY_UPDATED":
      return {
        ...state,
        categories: state.categories.map((c) =>
          c.id === action.category.id ? action.category : c,
        ),
      };

    case "CATEGORY_DELETED":
      return {
        ...state,
        categories: state.categories.filter((c) => c.id !== action.categoryId),
        habits: state.habits.map((h) =>
          h.category_id === action.categoryId ? { ...h, category_id: null } : h,
        ),
      };

    case "CATEGORIES_REORDERED":
      return { ...state, categories: action.categories };

    case "REFRESH":
      return {
        ...state,
        habits: action.habits,
        completions: action.completions,
        categories: action.categories,
        ...(action.settings ? { settings: action.settings } : {}),
      };

    default:
      return state;
  }
}
